import { Controller } from "@hotwired/stimulus"

/**
 * Generic Filter Search Controller
 * 
 * A reusable controller for all filter forms across the application.
 * Handles debounced search, clear functions, and proper button state management.
 * 
 * Configuration via data attributes:
 * - data-filter-search-debounce-value: Debounce delay (default: 150ms)
 * - data-turbo-frame: Target frame for updates (required for proper Turbo Frame updates)
 * 
 * IMPORTANT PITFALLS TO AVOID:
 * 
 * 1. NEVER trigger search in connect() or updateButtonStates()
 *    - connect() runs on page load and Turbo frame updates
 *    - This creates infinite loops when URL has existing parameters
 * 
 * 2. Use form_with with data-turbo-frame for proper Turbo Frame updates
 *    - Maintains focus on form elements during updates
 *    - Avoids full page refreshes that lose user context
 * 
 * 3. Handle Tom-select integration carefully
 *    - Always check for controller existence before accessing
 *    - Use controller methods, not direct DOM access
 *    - Tom-select can be in various states (loading, ready, destroyed)
 * 
 * 4. Separate state management from search logic
 *    - updateButtonStates() should ONLY update UI state
 *    - search() should ONLY handle user-initiated searches
 * 
 * 5. Event timing with requestSubmit()
 *    - requestSubmit() works with form_with to trigger Turbo Frame updates
 *    - Always clear timeouts before calling to prevent double submissions
 *    - Use debounce for user input, immediate submission for button actions
 */
export default class extends Controller {
    static targets = ["quickInput", "advancedInput", "advancedSection", "clearQuickBtn", "clearAllBtn"]
    static values = { debounce: { type: Number, default: 150 } }

    connect() {
        this.searchTimeout = null
        // Only update button states on connect, NEVER trigger search
        // Use setTimeout to ensure DOM is fully ready and avoid initialization loops
        setTimeout(() => {
            this.updateButtonStates()
        }, 0)
    }

    disconnect() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }
    }

    /**
     * This action is called by form inputs when users make changes.
     * Uses debounce to prevent excessive form submissions.
     * ONLY call this method in response to user interactions (input, change events).
     */
    search() {
        // Update button states first to provide immediate visual feedback
        this.updateButtonStates()

        // Clear any pending search to restart the debounce timer
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }

        // Schedule debounced search
        this.searchTimeout = setTimeout(() => {
            this.searchTimeout = null
            // requestSubmit() triggers form_with to update the Turbo Frame
            this.element.requestSubmit()
        }, this.debounceValue)
    }

    /**
     * Clears only the quick search input and triggers immediate search.
     * Provides instant feedback for better UX.
     */
    clearQuickSearch() {
        if (this.hasQuickInputTarget) {
            this.quickInputTarget.value = ''
            // Focus the input after clearing for better UX
            this.quickInputTarget.focus()
        }
        this.updateButtonStates()
        this.submitFormImmediately()
    }

    /**
     * Clears all form inputs and triggers immediate search.
     * Resets the entire form to its default state.
     */
    clearAllFilters() {
        // Clear quick search
        if (this.hasQuickInputTarget) {
            this.quickInputTarget.value = ''
        }

        // Clear all advanced inputs using generic clearing logic
        this.advancedInputTargets.forEach(input => {
            this.clearInput(input)
        })

        this.updateButtonStates()
        this.submitFormImmediately()
    }

    /**
     * Generic input clearing based on input type.
     * Handles regular selects, Tom-select enhanced selects, text inputs, and checkboxes.
     */
    clearInput(input) {
        if (input.tagName === 'SELECT') {
            // Check if it's a Tom-select enhanced select (like country select)
            const countryController = this.application.getControllerForElementAndIdentifier(input, 'country-select')
            if (countryController && countryController.tomSelect) {
                // Use the controller's clear method which handles Tom-select properly
                countryController.clear()
            } else {
                // Regular select - reset to first option (usually the "All" option)
                input.selectedIndex = 0
            }
        } else if (input.tagName === 'INPUT') {
            if (input.type === 'checkbox') {
                input.checked = false
            } else {
                // Text, email, date, etc. inputs
                input.value = ''
            }
        }
    }

    /**
     * Submits the form immediately without debounce.
     * Used by clear buttons and other immediate actions where users expect instant results.
     */
    submitFormImmediately() {
        // Cancel any pending debounced search to avoid double submissions
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }

        // Submit immediately - form_with will handle Turbo Frame update
        this.element.requestSubmit()
    }

    /**
     * Updates button states based on current form values.
     * CRITICAL: This method must NEVER trigger searches or form submissions.
     * It should ONLY update UI elements (button opacity, disabled status).
     * 
     * This provides visual feedback about form state without causing unwanted submissions.
     */
    updateButtonStates() {
        // Update quick search clear button
        if (this.hasClearQuickBtnTarget && this.hasQuickInputTarget) {
            const hasQuickValue = this.quickInputTarget.value.trim() !== ''
            this.updateButtonState(this.clearQuickBtnTarget, hasQuickValue)
        }

        // Update clear all button
        if (this.hasClearAllBtnTarget) {
            const hasAnyValue = this.hasAnyFormValues()
            this.updateButtonState(this.clearAllBtnTarget, hasAnyValue)
        }
    }

    /**
     * Checks if form has any non-default values.
     * IMPORTANT: This method must be read-only and never trigger events.
     * Used to determine if the "Clear All" button should be enabled.
     */
    hasAnyFormValues() {
        // Check quick search field
        const hasQuickSearch = this.hasQuickInputTarget && this.quickInputTarget.value.trim() !== ''

        // Check all advanced filter inputs
        const hasAdvancedFilters = this.advancedInputTargets.some(input => {
            return this.inputHasValue(input)
        })

        return hasQuickSearch || hasAdvancedFilters
    }

    /**
     * Generic value checking based on input type.
     * Returns true if the input has a non-default value.
     * Handles edge cases and errors gracefully.
     */
    inputHasValue(input) {
        try {
            if (input.tagName === 'SELECT') {
                // Check for Tom-select enhanced selects
                const countryController = this.application.getControllerForElementAndIdentifier(input, 'country-select')
                if (countryController && countryController.tomSelect) {
                    // Use Tom-select's getValue method safely
                    const value = countryController.tomSelect.getValue()
                    return value && value !== ''
                } else {
                    // Regular select - check if selection is not the first (default) option
                    // Most forms have "All" or empty option as index 0
                    return input.selectedIndex > 0
                }
            } else if (input.tagName === 'INPUT') {
                if (input.type === 'checkbox') {
                    return input.checked
                } else {
                    // Text, email, date, etc. inputs
                    return input.value.trim() !== ''
                }
            }
        } catch (error) {
            // If there's any error accessing the input, assume no value
            // This prevents crashes from accessing destroyed Tom-select instances
            console.warn('Error checking input value:', error)
            return false
        }
        return false
    }

    /**
     * Helper method to update button visual state.
     * Provides consistent styling across all buttons.
     */
    updateButtonState(button, isActive) {
        if (isActive) {
            button.style.opacity = '1'
            button.disabled = false
            button.style.cursor = 'pointer'
        } else {
            button.style.opacity = '0.5'
            button.disabled = true
            button.style.cursor = 'not-allowed'
        }
    }
}